import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';

import PersonalDetailsStep from './components/PersonalDetailsStep';
import EmploymentInformationStep from './components/EmploymentInformationStep';
import BeneficiarySetupStep from './components/BeneficiarySetupStep';
import DocumentUploadStep from './components/DocumentUploadStep';

const NasfundMemberRegistrationForms = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [registrationData, setRegistrationData] = useState({
    personalDetails: {},
    employmentInfo: {},
    beneficiaries: [],
    documents: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const steps = [
    { number: 1, title: 'Personal Details', icon: 'User', description: 'Employee information' },
    { number: 2, title: 'Employment Information', icon: 'Briefcase', description: 'Job and salary details' },
    { number: 3, title: 'Beneficiary Setup', icon: 'Users', description: 'Beneficiary allocation' },
    { number: 4, title: 'Document Upload', icon: 'Upload', description: 'Supporting documents' }
  ];

  const handleStepComplete = (stepNumber, data) => {
    setRegistrationData(prev => {
      const updatedData = { ...prev };
      switch (stepNumber) {
        case 1:
          updatedData.personalDetails = data;
          break;
        case 2:
          updatedData.employmentInfo = data;
          break;
        case 3:
          updatedData.beneficiaries = data;
          break;
        case 4:
          updatedData.documents = data;
          break;
        default:
          break;
      }
      return updatedData;
    });

    if (stepNumber < 4) {
      setCurrentStep(stepNumber + 1);
    } else {
      handleFinalSubmit();
    }
  };

  const handleFinalSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Simulate NASFUND registration submission
      console.log('NASFUND Registration Data:', registrationData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSuccess(true);
      
      // Navigate to workforce management after successful registration
      setTimeout(() => {
        navigate('/workforce-management-dashboard');
      }, 2000);
    } catch (err) {
      setError(err?.message || 'Failed to complete NASFUND registration');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All progress will be lost.')) {
      navigate('/workforce-management-dashboard');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalDetailsStep
            data={registrationData?.personalDetails}
            onComplete={(data) => handleStepComplete(1, data)}
            onCancel={handleCancel}
          />
        );
      case 2:
        return (
          <EmploymentInformationStep
            data={registrationData?.employmentInfo}
            personalDetails={registrationData?.personalDetails}
            onComplete={(data) => handleStepComplete(2, data)}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );
      case 3:
        return (
          <BeneficiarySetupStep
            data={registrationData?.beneficiaries}
            personalDetails={registrationData?.personalDetails}
            onComplete={(data) => handleStepComplete(3, data)}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );
      case 4:
        return (
          <DocumentUploadStep
            data={registrationData?.documents}
            registrationData={registrationData}
            onComplete={(data) => handleStepComplete(4, data)}
            onBack={handleBack}
            onCancel={handleCancel}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-emerald-100 p-4 rounded-full">
                <Icon name="CheckCircle" size={48} className="text-emerald-600" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              NASFUND Registration Complete!
            </h2>
            <p className="text-gray-600 mb-6">
              Employee has been successfully registered with NASFUND. Redirecting to workforce management...
            </p>
            <div className="animate-pulse">
              <Icon name="Loader" size={24} className="text-emerald-600 mx-auto" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-3 rounded-xl shadow-lg">
              <Icon name="FileText" size={32} className="text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            NASFUND Member Registration
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Complete employee enrollment for PNG superannuation compliance with streamlined data collection and automated validation
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => (
              <React.Fragment key={step?.number}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all ${
                      currentStep > step?.number
                        ? 'bg-emerald-600 text-white'
                        : currentStep === step?.number
                        ? 'bg-blue-600 text-white ring-4 ring-blue-200' :'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {currentStep > step?.number ? (
                      <Icon name="Check" size={20} />
                    ) : (
                      <Icon name={step?.icon} size={20} />
                    )}
                  </div>
                  <p
                    className={`mt-2 text-sm font-medium ${
                      currentStep >= step?.number ? 'text-gray-900' : 'text-gray-500'
                    }`}
                  >
                    {step?.title}
                  </p>
                  <p className="text-xs text-gray-500 hidden md:block">{step?.description}</p>
                </div>
                {index < steps?.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 transition-all ${
                      currentStep > step?.number ? 'bg-emerald-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="max-w-4xl mx-auto mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <Icon name="AlertCircle" size={20} className="text-red-600 mr-3 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Registration Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="max-w-4xl mx-auto">
          {renderStepContent()}
        </div>

        {/* NASFUND Information Banner */}
        <div className="max-w-4xl mx-auto mt-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <Icon name="Info" size={20} className="text-blue-600 mr-3 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900 mb-1">NASFUND Compliance Information</p>
                <p className="text-sm text-blue-700">
                  All PNG employers must register employees with NASFUND within 30 days of employment. 
                  Standard contribution rates: 6% employee, 8.4% employer. Ensure all information is accurate 
                  to avoid compliance penalties.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NasfundMemberRegistrationForms;